import {
  GET_ADMIN_BY_ID,
  GET_ADMIN_LIST,
  GET_ADMIN_ROLES_BY_ID,
  GET_ROLE_HISTORY,
} from "../constants";
import axiosInstance from "../api/index";
import swal from "sweetalert";
import { setMenuListData } from "../../reusableSlice";
import { clearAuth, setAuth, setProfile } from "../auth/slice";
import { clearStoredAuth } from "../../lib/auth";

const readError = (response) => {
  const data = response?.data ?? response;
  if (!data) return "Something went wrong. Please try again.";
  if (data.response?.message) return data.response.message;
  if (data.message) return data.message;
  if (data.error === "cannot_delete_self")
    return "You can't delete your own account.";
  if (typeof data.error === "string") return data.error;
  return "Something went wrong. Please try again.";
};

const readErrorFromException = (err) => {
  if (err?.response?.status === 403)
    return "You don't have permission to perform this action.";
  return readError(err?.response);
};

const successSwal = (text) =>
  swal({ text, icon: "success", button: "Close", timer: 1500 });

const errorSwal = (text) =>
  swal({ text, icon: "error", button: "Close", timer: 1800 });

export const addAdmin = (data, setLoading, navigate) => async () => {
  setLoading(true);
  try {
    const response = await axiosInstance.post(`admin/auth/admins`, data);
    if (response?.data?.status) {
      successSwal(response?.data?.response?.message || "Admin created.");
      navigate("/admin");
      return response;
    }
    errorSwal(readError(response));
  } catch (err) {
    errorSwal(readErrorFromException(err));
  } finally {
    setLoading(false);
  }
};

export const getAdminList = (data) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`admin/auth/admins`, {
      params: data,
    });
    dispatch({
      type: GET_ADMIN_LIST,
      adminList: response?.data?.status ? response.data.response : [],
    });
  } catch {
    dispatch({ type: GET_ADMIN_LIST, adminList: [] });
  }
};

export const getAdminById = (id) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`admin/auth/admins/${id}`);
    if (response?.data?.status) {
      dispatch({ type: GET_ADMIN_BY_ID, updateadmin: response.data.response });
      return response;
    }
    errorSwal(readError(response));
  } catch (err) {
    errorSwal(readErrorFromException(err));
  }
};

export const updateAdmin =
  (data, setLoading, navigate, { redirectTo = "/admin", isSelf = false } = {}) =>
  async (dispatch) => {
    setLoading(true);
    try {
      const response = await axiosInstance.patch(`admin/auth/admins`, data);
      if (response?.data?.status) {
        const { access_token, refresh_token, profile } =
          response.data.response || {};
        if (access_token || refresh_token) {
          dispatch(
            setAuth({
              token: access_token,
              refreshToken: refresh_token,
            }),
          );
        }
        if (isSelf && profile) {
          dispatch(setProfile(profile));
        }
        dispatch(getAdminById(data.id));
        successSwal(response?.data?.response?.message || "Saved.");
        if (redirectTo) navigate(redirectTo);
        return response;
      }
      errorSwal(readError(response));
    } catch (err) {
      errorSwal(readErrorFromException(err));
    } finally {
      setLoading(false);
    }
  };

export const updateAdminPermissions =
  (payload, setLoading) => async () => {
    setLoading?.(true);
    try {
      const response = await axiosInstance.patch(
        `admin/auth/admins/permissions`,
        payload,
      );
      if (response?.data?.status) {
        successSwal(
          response?.data?.response?.message || "Permissions updated.",
        );
        return response;
      }
      errorSwal(readError(response));
    } catch (err) {
      errorSwal(readErrorFromException(err));
    } finally {
      setLoading?.(false);
    }
  };

export const getAdminRolesById = (id, setLoading) => async (dispatch) => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.get(
      `admin/auth/admins/permissions?type=${id}`,
    );
    if (response?.data?.status) {
      dispatch({
        type: GET_ADMIN_ROLES_BY_ID,
        getadminroles: response.data.response,
      });
      return response;
    }
    errorSwal(readError(response));
  } catch (err) {
    errorSwal(readErrorFromException(err));
  } finally {
    setLoading?.(false);
  }
};

export const deleteAdmin = (id, setLoading, onSuccess) => async (dispatch) => {
  setLoading?.(true);
  try {
    const response = await axiosInstance.delete(`admin/auth/admins/${id}`);
    if (response?.data?.status) {
      successSwal(response?.data?.response?.message || "Admin deleted.");
      onSuccess?.();
      return response;
    }
    errorSwal(readError(response));
  } catch (err) {
    errorSwal(readErrorFromException(err));
  } finally {
    setLoading?.(false);
  }
};

// Fetch the immutable role-change audit log. The endpoint accepts the same
// snake_case filter conventions as the rest of the admin blueprint:
//   action, from_date, to_date, search_key, target_id, changed_by, page, per_page
// `pagination_meta` is only present when `page` is sent — we always send it
// so the UI can render pagination consistently.
export const getRoleHistory = (params) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`admin/auth/role-history`, {
      params,
    });
    dispatch({
      type: GET_ROLE_HISTORY,
      roleHistory: response?.data?.status
        ? response.data.response
        : { history: [], pagination_meta: null },
    });
  } catch {
    dispatch({
      type: GET_ROLE_HISTORY,
      roleHistory: { history: [], pagination_meta: null },
    });
  }
};

// Fetch the total count of active super admins. Used to disable the Demote
// toggle when only one super remains (backend will 422 either way, this is a
// pre-emptive UX guard). Returns null on failure so callers can fail-open.
export const getActiveSuperAdminCount = async () => {
  try {
    const response = await axiosInstance.get(`admin/auth/admins`, {
      params: { is_super_admin: true, status: 1, page: 1 },
    });
    if (!response?.data?.status) return null;
    const data = response.data.response || {};
    const total = data.pagination_meta?.total;
    if (Number.isFinite(Number(total))) return Number(total);
    return Array.isArray(data.admins) ? data.admins.length : null;
  } catch {
    return null;
  }
};

// Promote/demote an admin's super-admin status. Backend returns
// { changed: bool, is_super_admin: bool }. When changed=false we skip the
// toast (no-op). When the target is the current browser session, the JWT is
// dead — clear auth and redirect to login.
export const updateAdminRole =
  (
    adminId,
    isSuperAdmin,
    { setLoading, isSelf = false, onSuccess } = {},
  ) =>
  async (dispatch) => {
    setLoading?.(true);
    try {
      const response = await axiosInstance.post(
        `admin/auth/admins/${adminId}/role`,
        { is_super_admin: Boolean(isSuperAdmin) },
      );
      if (response?.data?.status) {
        const { changed, message } = response.data.response || {};
        if (changed === false) return response;
        if (isSelf) {
          dispatch(clearAuth());
          clearStoredAuth();
          window.location.href = `${process.env.PUBLIC_URL}/`;
          return response;
        }
        successSwal(message || "Role updated successfully");
        onSuccess?.();
        return response;
      }
      errorSwal(readError(response));
    } catch (err) {
      errorSwal(readErrorFromException(err));
    } finally {
      setLoading?.(false);
    }
  };

export const getAllMenuList = (data) => async (dispatch) => {
  try {
    const response = await axiosInstance.get(`admin/auth/menu`, {
      params: data,
    });
    dispatch(setMenuListData(response?.data?.response || []));
  } catch {
    dispatch(setMenuListData([]));
  }
};
